import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  createHighlight,
  createPlaybackSession,
  deleteHighlight,
  getHighlight,
  getUpload,
  getUploadView,
  listUploads,
  playerRequest,
  saveUploadView,
  updateHighlight,
  updateUpload
} from '../service'
import VideoJS from '../components/video/VideoJS'
import PlayerTrackOverlay from '../components/video/PlayerTrackOverlay'
import AIReview from '../components/video/AIReview'
import AIEvent from '../components/video/AIEvent'
import UploadTimeline from '../components/video/UploadTimeline'
import useUploadReview from '../hooks/useUploadReview'
import useAdminStatus from '../hooks/useAdminStatus'
import { applyPlaybackQuality } from '../components/video/playbackQuality'
import { VideoViewAdjuster, VideoViewMenu } from '../components/video/VideoViewControls'
import { FULL_VIEW, clampView } from '../components/video/videoView'
import useClippingMode from '../hooks/useClippingMode'
import '../components/video/clipping.css'
import TrapFocus from '@mui/material/Unstable_TrapFocus'
import { focusPlayerForShortcuts, seekPlayerToHighlight } from '../components/video/seek'
import 'videojs-overlay'
import 'videojs-overlay/dist/videojs-overlay.css'
import HighlightDialog from '../components/highlight/HighlightDialog'
import HighlightCard from '../components/card/HighlightCard'
import PlayerStatusCard from '../components/card/PlayerStatusCard'
import { Alert, Autocomplete, Box, Button, Chip, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import UploadDialog from '../components/upload/UploadDialog'
import ControlsDialog from '../components/video/ControlsDialog'
import UploadCard from '../components/card/UploadCard'
import Messages from '../components/common/Messages'
import Comparator from '../components/common/Comparator'
import UploadProcessingStatus from '../components/upload/UploadProcessingStatus'
import PlayerReferences, { captureReferenceFrame } from '../components/video/PlayerReferences'
import VideoAnalysis from '../components/video/VideoAnalysis'
import ClipCoachingStatus from '../components/video/ClipCoachingStatus'
import { clipRunLabel, highlightForRange } from '../service/coaching'
import { useAuth } from '../auth/AuthContext'
import { timelineEntries as buildTimelineEntries, timestamp } from '../components/video/uploadTimeline'
import { aiHighlightDraft, savedHighlightMessage } from '../service/aiHighlights'
import { gamePartsFor, partLabel } from '../service/gameParts'
import { MERGED_PART_NOTICE, mergedPartLock, mergedPartRedirect, stayOnPartQuery } from '../service/combine'
import { notePlaybackSession, playbackSessionReplaced, shouldRenewPlayback } from '../service/playbackRenewal'
import { localVideoView, rememberLocalVideoView } from '../service/videoViewStore'
import { useSetting } from '../settings/SettingsContext'
import { CLIP_END_BEHAVIOR } from '../constants/settings'

// Visible text for narrow widths; the button's aria-label carries the full name.
// `early` switches sooner, for the part navigation that only some uploads show.
const ShortLabel = ({ full, short, early = false }) => <>
  <span className={early ? 'editor-label-full-early' : 'editor-label-full'}>{full}</span>
  <span className={early ? 'editor-label-short-early' : 'editor-label-short'} aria-hidden="true">{short}</span>
</>

ShortLabel.propTypes = {
  full: PropTypes.string.isRequired,
  short: PropTypes.string.isRequired,
  early: PropTypes.bool
}

// Signed-in playback cookies are shared by every tab and video, and the session answered last wins.
// Each session is noted so another tab can tell when its cookies were taken (playbackRenewal.js).
const issuePlaybackSession = async uploadId => {
  const playback = await createPlaybackSession(uploadId)
  notePlaybackSession(uploadId)
  return playback
}

// A session for an earlier video (a renewal or a load still in flight when the viewer moved on) can answer
// after the video on screen got its own, and its cookies then win. The video on screen asks again.
const reclaimPlaybackSession = async (session, answeredFor) => {
  const current = session.current
  if (!current || current.uploadId === answeredFor) return
  const playback = await issuePlaybackSession(current.uploadId)
  if (session.current?.uploadId === current.uploadId) {
    session.current = { uploadId: current.uploadId, expiresAt: playback.expiresAt }
  }
  await reclaimPlaybackSession(session, current.uploadId)
}

const Upload = () => {
  // eslint-disable-next-line no-unused-vars
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [message, setMessage] = useState(null)
  const [uploadId, setUploadId] = useState(params.uploadId)
  const [highlightId, setHighlightId] = useState(params.highlightId)
  const [uploads, setUploads] = useState(null)
  const { user } = useAuth()
  const [upload, setUpload] = useState(null)
  const [playbackUrl, setPlaybackUrl] = useState(null)
  // The upload and expiry of the latest playback session, and whether a renewal is in flight.
  const playbackSession = useRef(null)
  const renewingPlayback = useRef(false)
  const [highlights, setHighlights] = useState([])
  const [highlight, setHighlight] = useState(null)
  const [currentTime, setCurrentTime] = useState(null)
  const [currentRate, setCurrentRate] = useState(1)
  const [currentRemaining, setCurrentRemaining] = useState(null)
  const [currentQuality, setCurrentQuality] = useState(null)
  const [newHighlight, setNewHighlight] = useState(null)
  const [editingUpload, setEditingUpload] = useState(false)
  const [editingHighlight, setEditingHighlight] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [eventsPaused, setEventsPaused] = useState(false)
  const markRef = useRef(null)
  const [mark, setMark] = useState(null)
  const clipping = useClippingMode()
  const playerRef = useRef(null)
  const highestQualityRef = useRef(false)
  const [highestQuality, setHighestQuality] = useState(false)
  // The video's view (one half of the ice, or a manual zoom). Owners and contributors save it for
  // everyone; other viewers keep their own on this device.
  const [videoView, setVideoView] = useState(FULL_VIEW)
  // What the player does once a marked clip is saved (Settings): by default it stays paused at
  // the clip's end; the highlight is selected either way, so it can be replayed in one click.
  const clipEndBehavior = useSetting(CLIP_END_BEHAVIOR)
  const keepPlayheadForHighlight = useRef(null)
  const [viewCanEdit, setViewCanEdit] = useState(false)
  const [adjustingView, setAdjustingView] = useState(false)
  const viewSaveTimer = useRef(null)
  const confirm = useConfirm()
  const [view, setView] = useState('watch')
  const [selectedEntryId, setSelectedEntryId] = useState(null)
  const [panelOpen, setPanelOpen] = useState(true)
  const [sourcePicker, setSourcePicker] = useState(false)
  const [referenceCapture, setReferenceCapture] = useState(null)
  const [referenceContext, setReferenceContext] = useState(null)
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const [analysisReview, setAnalysisReview] = useState(null)
  const [analysisJobId, setAnalysisJobId] = useState(null)
  const [analysisFeedback, setAnalysisFeedback] = useState({})
  const [analysisExtras, setAnalysisExtras] = useState({})
  const isAdmin = useAdminStatus()
  const [analysisRange, setAnalysisRange] = useState(null)
  const [analysisTracks, setAnalysisTracks] = useState([])
  const [clipCoachingRefresh, setClipCoachingRefresh] = useState(0)
  const openedCoaching = useRef(null)
  const analysisClipEnd = useRef(null)
  const closeReferences = () => { setReferenceCapture(null); setEventsPaused(false); focusPlayerForShortcuts(playerRef.current) }
  const identifyPlayer = () => {
    try {
      const capture = captureReferenceFrame(playerRef.current)
      setEventsPaused(true); setReferenceCapture(capture)
    } catch (error) { setMessage({ severity: 'error', text: error.message }) }
  }
  const reviewState = useUploadReview(uploadId)
  // While a part's combined video is queued, processing or ready, the part's highlights can't change (D34).
  const partLock = mergedPartLock(upload)
  // Opens one analysis run's report: from the dialog, a clip's "View coaching", or ?coaching=<run>.
  const openJobReport = detail => {
    const result = detail?.result || {}
    setAnalysisReview(result.review || null); setAnalysisJobId(detail?.id || null); setAnalysisFeedback(result.feedback || {})
    setAnalysisExtras({
      context: detail?.context,
      context_used: result.context,
      context_digest: detail?.context_digest,
      coaching_revision: detail?.coaching_revision,
      coaching_feedback: detail?.coaching_feedback || [],
      usage_report: detail?.usage_report,
      clip: Boolean(detail?.clip),
      start_seconds: detail?.start_seconds,
      end_seconds: detail?.end_seconds
    })
    playerRef.current?.pause(); setView('report')
  }
  const timelineReview = analysisReview || reviewState.data?.review
  const reportData = analysisReview ? { review: analysisReview, feedback: analysisFeedback, job_id: analysisJobId, ...analysisExtras } : reviewState.data
  const timelineItems = buildTimelineEntries(highlights, timelineReview)
  const selectedEntry = timelineItems.find(entry => entry.id === selectedEntryId) || null
  const seekReview = seconds => {
    analysisClipEnd.current = null
    const player = playerRef.current
    if (!player || player.isDisposed() || player.readyState() < 1) return false
    setView('watch')
    seekPlayerToHighlight(player, { start: seconds })
    player.play()?.catch(() => {})
    requestAnimationFrame(() => focusPlayerForShortcuts(player))
    return true
  }
  const saveAIHighlight = (event, playerId = event.playerId || '') => {
    if (partLock) {
      setMessage({ severity: 'info', text: partLock })
      return
    }
    setEventsPaused(true)
    playerRef.current?.pause()
    // A job's clips and report carry its ID; the upload's job-less review is identified by its run.
    const jobId = event.analysisJobId || (analysisReview ? analysisJobId : null)
    const runId = jobId ? null : timelineReview?.run_id
    setNewHighlight(aiHighlightDraft(event, { playerId, analysisJobId: jobId, runId, groups: upload.groups || [] }))
  }
  const timelineProps = {
    highlights,
    review: timelineReview,
    duration: currentTime !== null && currentRemaining !== null ? currentTime + currentRemaining : undefined,
    currentTime,
    selectedId: selectedEntryId,
    onHighlight: clip => highlightSelected(null, clip),
    onSeek: seekReview,
    onSelect: entry => { setSelectedEntryId(entry.id); setPanelOpen(true) }
  }

  /**
   * Load the upload id from the router.
   */
  useEffect(() => {
    if (params.uploadId !== uploadId) {
      setUploadId(params.uploadId)
    }
    if (params.highlightId !== highlightId) {
      setHighlightId(params.highlightId)
    }
  }, [params])

  /**
   * Load the uploads from the server.
   */
  useEffect(() => {
    const init = async () => {
      const data = await listUploads()
      setUploads(data)
    }
    init()
  }, [])

  useEffect(() => {
    setSelectedEntryId(highlightId ? `highlight:${highlightId}` : null)
  }, [uploadId, highlightId])

  // Each video opens in its saved view; a viewer who can't edit it may have one of their own.
  useEffect(() => {
    flushViewSave()
    setAdjustingView(false)
    setVideoView(FULL_VIEW)
    setViewCanEdit(false)
    if (!uploadId) return undefined
    let cancelled = false
    getUploadView(uploadId)
      .then(({ view: saved, canEdit }) => {
        if (cancelled) return
        setViewCanEdit(canEdit)
        const own = canEdit ? null : localVideoView(uploadId)
        setVideoView(own || saved)
      })
      .catch(err => {
        // Without the saved view the whole frame still plays.
        console.warn('Unable to load the video view.', err)
        if (!cancelled) setVideoView(localVideoView(uploadId) || FULL_VIEW)
      })
    return () => { cancelled = true }
  }, [uploadId])

  useEffect(() => () => flushViewSave(), [])

  // Arrived from an old link to a combined part: say so once, then drop the state so a reload stays quiet.
  useEffect(() => {
    if (!location.state?.mergedPart) return
    setMessage({ severity: 'info', text: MERGED_PART_NOTICE })
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [location.key])

  // ?coaching=<run> (Admin > recent runs) opens that run's report once the upload has loaded.
  useEffect(() => {
    const jobId = new URLSearchParams(window.location.search).get('coaching')
    if (!jobId || upload?.id !== uploadId || openedCoaching.current === `${uploadId}:${jobId}`) return
    openedCoaching.current = `${uploadId}:${jobId}`
    playerRequest(`/uploads/${uploadId}/analysis/${encodeURIComponent(jobId)}`)
      .then(openJobReport)
      .catch(() => setMessage({ severity: 'error', text: 'That analysis run could not be opened. It may belong to another account or have been removed.' }))
  }, [upload, uploadId])

  /**
   * Set the current upload.
   */
  useEffect(() => {
    let cancelled = false

    if (uploadId) {
      const loadUpload = async () => {
        try {
          setPlaybackUrl(null)
          playbackSession.current = null
          setUpload(null)
          setReferenceCapture(null)
          setReferenceContext(null)
          setAnalysisOpen(new URLSearchParams(window.location.search).has('analysis'))
          setAnalysisReview(null); setAnalysisJobId(null); setAnalysisExtras({})
          setAnalysisFeedback({}); setAnalysisTracks([]); setAnalysisRange(null)
          analysisClipEnd.current = null
          setEventsPaused(false)
          setHighlights([])
          setSelectedEntryId(highlightId ? `highlight:${highlightId}` : null)
          setView('watch')
          setCurrentTime(null)
          markRef.current = null
          setMark(null)
          const u = await getUpload(uploadId)
          if (cancelled) return

          // A part whose combined video is ready opens the combined video, at the copy of the linked highlight (D34).
          const combined = mergedPartRedirect(u, window.location.search, highlightId)
          if (combined) {
            navigate(combined, { replace: true, state: { mergedPart: true } })
            return
          }

          document.title = `Highlights - ${u.name}`
          setUpload(u)
          const sortedHighlights = [...u.highlights.items].sort(Comparator.NumberComparator('start'))
          setHighlights(sortedHighlights)

          if (u.readyForPlayback) {
            const playback = await issuePlaybackSession(u.id)
            if (!cancelled) {
              playbackSession.current = { uploadId: u.id, expiresAt: playback.expiresAt }
              setPlaybackUrl(playback.url)
            } else {
              await reclaimPlaybackSession(playbackSession, u.id)
            }
          }
        } catch (err) {
          console.error(err)
          if (!cancelled) {
            setMessage({
              severity: 'error',
              text: 'Error retrieving upload or authorizing video playback.'
            })
          }
        }
      }
      loadUpload()
    } else {
      setUpload(null)
      setPlaybackUrl(null)
      playbackSession.current = null
      setHighlights([])
      setCurrentTime(null)
      markRef.current = null
      setMark(null)
    }

    return () => {
      cancelled = true
    }
  }, [uploadId])

  useEffect(() => {
    if (!uploadId || !upload || upload.readyForPlayback || upload.status === 'failed') return undefined

    let cancelled = false
    const refreshProcessingState = async () => {
      try {
        const current = await getUpload(uploadId)
        if (cancelled) return

        setUpload(current)
        setHighlights([...current.highlights.items].sort(Comparator.NumberComparator('start')))
        setUploads(existing => existing
          ? existing.map(item => item.id === current.id ? current : item)
          : existing)

        if (current.readyForPlayback) {
          const playback = await issuePlaybackSession(current.id)
          if (!cancelled) {
            playbackSession.current = { uploadId: current.id, expiresAt: playback.expiresAt }
            setPlaybackUrl(playback.url)
          } else {
            await reclaimPlaybackSession(playbackSession, current.id)
          }
        }
      } catch (err) {
        console.error('Unable to refresh upload processing state.', err)
      }
    }

    const timer = window.setInterval(refreshProcessingState, 10000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [uploadId, upload?.status, upload?.readyForPlayback])

  /**
   * Renew the playback cookies when the tab comes back near or after their expiry (a paused game,
   * a sleeping laptop), or after another tab took them for a different video. Only the cookies
   * change: the player keeps its source and position.
   */
  useEffect(() => {
    if (!playbackUrl || !upload?.id) return undefined
    const renew = async () => {
      const session = playbackSession.current
      if (document.visibilityState !== 'visible' || renewingPlayback.current || session?.uploadId !== upload.id) return
      if (!shouldRenewPlayback(session.expiresAt, Date.now()) && !playbackSessionReplaced(upload.id)) return
      renewingPlayback.current = true
      try {
        const playback = await issuePlaybackSession(upload.id)
        if (playbackSession.current?.uploadId === upload.id) {
          playbackSession.current = { uploadId: upload.id, expiresAt: playback.expiresAt }
        } else {
          await reclaimPlaybackSession(playbackSession, upload.id)
        }
      } catch (err) {
        console.error('Unable to renew the playback session.', err)
      } finally {
        renewingPlayback.current = false
      }
    }
    document.addEventListener('visibilitychange', renew)
    window.addEventListener('focus', renew)
    return () => {
      document.removeEventListener('visibilitychange', renew)
      window.removeEventListener('focus', renew)
    }
  }, [playbackUrl, upload?.id])

  /**
   * Load the current highlight.
   */
  useEffect(() => {
    let cancelled = false
    if (highlightId) {
      const loadHighlight = async () => {
        try {
          const h = await getHighlight(highlightId)
          if (cancelled) return
          setHighlight(h)
        } catch (err) {
          console.error(err)
          setMessage({
            severity: 'error',
            text: 'Error retrieving highlight'
          })
        }
      }
      loadHighlight()
    } else {
      setHighlight(null)
    }
    return () => { cancelled = true }
  }, [highlightId])

  useEffect(() => {
    if (!highlight || !playerRef.current) return
    // A clip that was just marked keeps the playhead where it is, unless the setting rewinds.
    if (keepPlayheadForHighlight.current === highlight.id) {
      keepPlayheadForHighlight.current = null
      return
    }
    seekPlayerToHighlight(playerRef.current, highlight)
  }, [highlight])

  const pushUrlState = (newUploadId, newHighlightId) => {
    const base = '/upload'
    if (!newUploadId) {
      navigate(base)
    } else {
      // On an original part opened with ?original=1 (or a report), stay there across reloads and Back (D34).
      const query = newUploadId === uploadId ? stayOnPartQuery(upload, window.location.search) : ''
      const baseWithUpload = `${base}/${newUploadId}`
      if (newHighlightId) {
        navigate(`${baseWithUpload}/highlight/${newHighlightId}${query}`)
      } else {
        navigate(`${baseWithUpload}${query}`)
      }
    }
  }

  // The API's reason for a refused highlight write. A 409 means this part was combined into a full game
  // video since the page loaded (D34): reload the upload so the page locks its controls and shows the banner.
  const highlightWriteError = (err, fallback) => {
    if (err?.status === 409 && uploadId) {
      getUpload(uploadId)
        .then(current => setUpload(shown => shown?.id === current.id ? current : shown))
        .catch(error => console.error(error))
    }
    return err?.detail || fallback
  }

  const uploadSelected = (ev, newValue) => {
    pushUrlState(newValue ? newValue.id : null)
  }

  const highlightSelected = (ev, newValue) => {
    pushUrlState(uploadId, newValue ? newValue.id : null)
    focusPlayerForShortcuts(playerRef.current)
  }

  // Save the view a moment after the last change, so dragging sends one request. A save still
  // pending when the page moves to another video is sent at once rather than dropped.
  const pendingViewSave = useRef(null)
  const flushViewSave = () => {
    clearTimeout(viewSaveTimer.current)
    const pending = pendingViewSave.current
    pendingViewSave.current = null
    if (!pending) return
    if (!pending.canEdit) {
      rememberLocalVideoView(pending.id, pending.view)
      return
    }
    saveUploadView(pending.id, pending.view).catch(err => {
      console.error('Unable to save the video view.', err)
      rememberLocalVideoView(pending.id, pending.view)
      setMessage({ severity: 'warning', text: 'The view couldn’t be saved for everyone; it is kept on this device for now.' })
    })
  }

  const changeVideoView = next => {
    const view = clampView(next)
    setVideoView(view)
    pendingViewSave.current = { id: uploadId, view, canEdit: viewCanEdit }
    clearTimeout(viewSaveTimer.current)
    viewSaveTimer.current = setTimeout(flushViewSave, 700)
  }

  const handlePlayerReady = (player) => {
    playerRef.current = player
    const levels = player.qualityLevels()
    const apply = () => applyPlaybackQuality(levels, highestQualityRef.current)
    levels.on('addqualitylevel', apply)
    apply()
    player.one('dispose', () => levels.off('addqualitylevel', apply))
    if (highlight) {
      seekPlayerToHighlight(player, highlight)
    }
  }

  const handleLoadMetaData = () => {
    if (playerRef.current && highlight) {
      seekPlayerToHighlight(playerRef.current, highlight)
    }
  }

  const formatTime = time => {
    const multiplier = Math.pow(10, 1 || 0)
    return Math.round(time * multiplier) / multiplier
  }

  const timeUpdated = ct => {
    if (analysisClipEnd.current !== null && ct >= analysisClipEnd.current) {
      analysisClipEnd.current = null
      playerRef.current?.pause()
    }
    setCurrentTime(ct)
    setCurrentRemaining(formatTime(playerRef.current.duration() - ct))
  }

  const rateUpdated = rate => {
    setCurrentRate(formatTime(rate))
  }

  const qualityChanged = quality => {
    setCurrentQuality(quality)
  }

  const markSet = async time => {
    if (!Number.isFinite(time) || !playerRef.current || !upload) return
    if (partLock) {
      setMessage({ severity: 'info', text: partLock })
      return
    }
    if (markRef.current !== null) {
      // Completed a mark
      const start = markRef.current
      const end = formatTime(time)
      if (start === end) {
        setMessage({
          severity: 'error',
          text: 'Invalid mark, the start is the same as the end.'
        })
        return // **EXIT**
      } else if (end < start) {
        setMessage({
          severity: 'error',
          text: 'Invalid mark, the end is before the start.'
        })
        return
      }

      setEventsPaused(true)

      if (playerRef.current.isFullscreen()) {
        playerRef.current.exitFullscreen()
      }
      playerRef.current.pause()
      setNewHighlight({
        start,
        end,
        groups: upload && upload.groups ? [...upload.groups] : []
      })
    } else {
      markRef.current = formatTime(time)
      setMark(markRef.current)
    }
  }

  const onEditUpload = () => {
    setEventsPaused(true)
    if (playerRef.current) {
      playerRef.current.pause()
    }
    setEditingUpload(true)
  }
  const onSaveUpload = async (ev, updated) => {
    try {
      const result = await updateUpload(updated)
      setMessage({
        severity: 'success',
        text: `Successfully Updated Upload: ${updated.name}`
      })
      setUpload(result)
      for (let i = 0; i < uploads.length; i++) {
        if (uploads[i].id === result.id) {
          uploads[i] = result
          break
        }
      }
      setEditingUpload(false)
    } catch (err) {
      console.error(err)
      setMessage({
        severity: 'error',
        text: `Error while updating Upload: ${updated.name}`
      })
    } finally {
      setEventsPaused(false)
    }
  }

  const onCancelSaveUpload = () => {
    setEditingUpload(false)
    setEventsPaused(false)
  }

  const onCreateHighlight = async (ev, data) => {
    try {
      if (!data.name) {
        setMessage({
          severity: 'error',
          text: 'Highlight name is a required field.'
        })
      }
      const created = await createHighlight(Object.assign(
        {},
        data,
        {
          uploadHighlightsId: upload.id
        }
      ))
      console.log(`Created new highlight with id: ${created.id}`)
      markRef.current = null
      setMark(null)
      setHighlights(current => [...current.filter(item => item.id !== created.id), created].sort(Comparator.NumberComparator('start')))
      setNewHighlight(null)
      setEventsPaused(false)
      if (clipEndBehavior !== 'rewind_to_start') keepPlayheadForHighlight.current = created.id
      pushUrlState(uploadId, created.id)
      if (clipEndBehavior === 'keep_playing') playerRef.current?.play()?.catch(() => {})
      focusPlayerForShortcuts(playerRef.current)

      setMessage({
        severity: created.saveResult === 'kept' ? 'info' : 'success',
        text: savedHighlightMessage(created)
      })
    } catch (err) {
      console.error(err)
      setMessage({
        severity: 'error',
        text: highlightWriteError(err, 'Error while creating highlight.')
      })
    }
  }

  const onCancelCreateHighlight = () => {
    setEventsPaused(false)
    setNewHighlight(null)
    markRef.current = null
    setMark(null)
  }

  const onEditHighlight = (target = highlight) => {
    if (target && partLock) {
      setMessage({ severity: 'info', text: partLock })
      return
    }
    if (target) {
      setHighlight(target)
      setEventsPaused(true)
      if (playerRef.current) {
        playerRef.current.pause()
      }
      setEditingHighlight(true)
    }
  }

  const onDeleteHighlight = async (target = highlight) => {
    if (!target || partLock) return
    setEventsPaused(true)
    playerRef.current?.pause()
    try {
      await confirm({ description: `Are you sure you want to delete highlight: ${target.name}` })
    } catch (_) {
      setEventsPaused(false)
      return
    }
    try {
      // Find the index of the deleted highlight.
      const index = highlights.findIndex(item => item.id === target.id)
      // Determine the id of the new current highlight.
      let newHighlightId = null
      if (index === 0 && highlights.length > 1) {
        newHighlightId = highlights[1].id
      } else if (highlights.length > 1) {
        newHighlightId = highlights[index - 1].id
      }
      await deleteHighlight(target.id)
      // Remove the deleted highlight from the list.
      setHighlights(current => current.filter(item => item.id !== target.id))
      setSelectedEntryId(newHighlightId ? `highlight:${newHighlightId}` : null)
      if (target.id === highlightId) setHighlightId(null)
      pushUrlState(uploadId, newHighlightId)
    } catch (err) {
      console.log(err)
      setMessage({
        severity: 'error',
        text: highlightWriteError(err, 'Error deleting highlight.')
      })
    } finally {
      setEventsPaused(false)
    }
  }

  const onRefreshHighlight = (target = highlight) => {
    if (target) seekPlayerToHighlight(playerRef.current, target)
  }

  const onPrev = () => {
    const index = highlights.findIndex(item => item.id === highlight.id)
    if (index - 1 >= 0) {
      pushUrlState(uploadId, highlights[index - 1].id)
    }
  }

  const onNext = () => {
    const index = highlights.findIndex(item => item.id === highlight.id)
    if (index + 1 < highlights.length) {
      pushUrlState(uploadId, highlights[index + 1].id)
    }
  }

  const onSaveHighlight = async (ev, data) => {
    try {
      const updated = await updateHighlight(data)
      console.log(`Updated highlight with id: ${updated.id}`)
      const refreshed = await getHighlight(data.id)
      setHighlight(refreshed)
      setHighlights(current => [...current.filter(item => item.id !== refreshed.id), refreshed].sort(Comparator.NumberComparator('start')))
      setEventsPaused(false)
      setEditingHighlight(false)

      setMessage({
        severity: 'success',
        text: `Successfully saved highlight: ${updated.name}`
      })
    } catch (err) {
      console.error(err)
      console.dir(data)
      setMessage({
        severity: 'error',
        text: highlightWriteError(err, `Unable to save higlight: ${data.name}`)
      })
    }
  }

  const onCancelSaveHighlight = () => {
    setEventsPaused(false)
    setEditingHighlight(false)
  }

  const cleared = () => {
    if (markRef.current !== null) {
      markRef.current = null
      setMark(null)
    }
  }

  const onShowHelp = () => {
    setEventsPaused(true)
    setShowHelp(true)
  }

  const onHelpClose = () => {
    setEventsPaused(false)
    setShowHelp(false)
  }

  // Part navigation only for a game still stored as separate parts (D34); any other video in a game links to its details.
  const gameParts = gamePartsFor(uploads, upload?.gameId)
  const partIndex = gameParts.length > 1 ? gameParts.findIndex(part => part.id === upload?.id) : -1
  const nextPart = partIndex >= 0 ? gameParts[partIndex + 1] : null

  return (
    <>
      {referenceCapture && upload && <PlayerReferences key={upload.id} uploadId={upload.id} capture={referenceCapture} initialContext={referenceContext} onClose={closeReferences} onSaved={saved => {
        closeReferences(); setReferenceContext(null)
        setAnalysisRange({ start: 0, playerId: saved.player_id, gameTeam: saved.team, gameJersey: saved.jersey, gameUniform: saved.uniform, gameSide: referenceContext?.side, gamePosition: referenceContext?.position })
        setAnalysisOpen(true)
      }} onSeek={seconds => {
        setView('watch'); seekPlayerToHighlight(playerRef.current, { start: seconds }); playerRef.current?.pause()
      }} />}
      <TrapFocus open={clipping.active && !eventsPaused} disableAutoFocus>
      <Stack
        spacing={1}
        tabIndex={-1}
        className={`editor-workspace editor-modern-workspace${clipping.active ? ' editor-workspace-clipping' : view === 'watch' ? ' editor-workspace-watch' : ''}`}
        sx={{ maxWidth: 1920, mx: 'auto' }}
      >
        {referenceContext && !referenceCapture && <Alert severity="info" action={<Stack direction="row">
          <Button disabled={currentTime === null} onClick={identifyPlayer}>Use this frame</Button>
          <Button onClick={() => { setReferenceContext(null); setAnalysisOpen(true) }}>Back to matching</Button>
        </Stack>}>Optional player reference: use the video controls to find one clear view, pause, then choose “Use this frame”. You only need to show the player once for this recording.</Alert>}
        {/* One slim bar: the title, then the view switch and actions. Short labels are only visual; each button keeps its full name. */}
        <Paper className="editor-upload-header" variant="outlined">
          <Typography variant="h6" className="editor-upload-name" title={upload?.name || undefined}>{upload?.name || 'Choose a video'}</Typography>
          <Box className="editor-upload-actions">
            {partIndex >= 0
              ? <>
                  <Button size="small" component={RouterLink} to={`/game/${upload.gameId}`} aria-label={`All game parts (${gameParts.length})`}><ShortLabel early full={`All game parts (${gameParts.length})`} short={`Parts (${gameParts.length})`} /></Button>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{partLabel(gameParts[partIndex], gameParts)}</Typography>
                  {nextPart && <Button size="small" component={RouterLink} to={`/upload/${nextPart.id}`} aria-label="Next part"><ShortLabel early full="Next part" short="Next" /></Button>}
                </>
              : upload?.gameId && <Button size="small" component={RouterLink} to={`/game/${upload.gameId}`}>Game details</Button>}
            <Box className="editor-view-switch" role="group" aria-label="View">
              <Button size="small" variant={view === 'watch' ? 'contained' : 'text'} aria-pressed={view === 'watch'} onClick={() => setView('watch')}>Watch & clip</Button>
              <Button size="small" variant={view === 'report' ? 'contained' : 'text'} aria-pressed={view === 'report'} onClick={() => { playerRef.current?.pause(); setView('report'); reviewState.retry() }}>AI report</Button>
            </Box>
            <Box className="editor-upload-spacer" />
            <Button size="small" aria-label="Identify player" disabled={!playbackUrl || currentTime === null} onClick={identifyPlayer}><ShortLabel full="Identify player" short="Identify" /></Button>
            <Button size="small" variant="outlined" aria-label="Player tracking & analysis" disabled={!playbackUrl} onClick={() => { setAnalysisRange(null); setAnalysisOpen(true) }}><ShortLabel full="Player tracking & analysis" short="Tracking" /></Button>
            <Button size="small" aria-label="Change upload" onClick={() => setSourcePicker(n => !n)} aria-expanded={sourcePicker}><ShortLabel full="Change upload" short="Change" /></Button>
            {/* Hidden rather than removed in the report view, so the other buttons stay put. */}
            <Button size="small" aria-label={panelOpen ? 'Hide events' : 'Show events'} aria-expanded={panelOpen} aria-controls="play-browser" onClick={() => setPanelOpen(n => !n)} sx={{ visibility: view === 'watch' ? 'visible' : 'hidden' }}><ShortLabel full={panelOpen ? 'Hide events' : 'Show events'} short="Events" /></Button>
          </Box>
        </Paper>
        {upload?.mergedInto && upload.mergedInto.status !== 'failed' && <Alert
          severity="info"
          action={upload.mergedInto.readyForPlayback
            ? <Button size="small" component={RouterLink} to={`/upload/${upload.mergedInto.id}`}>Open full game</Button>
            : undefined}
        >
          {upload.mergedInto.readyForPlayback
            ? `This is an original part of '${upload.mergedInto.name}'.`
            : `This part is being combined into one video ('${upload.mergedInto.name}').`}
        </Alert>}
        {(sourcePicker || !uploadId) && <Paper
          className="editor-source-selectors"
          variant="outlined"
          sx={{
            px: { xs: 1, md: 1.25 },
            py: 1,
            background: 'linear-gradient(180deg, rgba(255,255,255,.025), rgba(255,255,255,.008))'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={0.75}
            alignItems="stretch"
          >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {uploads
                  ? <Autocomplete
                      disablePortal
                      id="upload-select"
                      size="small"
                      isOptionEqualToValue={(option, value) => Boolean(value && option.id === value.id)}
                      getOptionLabel={option => option.name}
                      value={upload || null}
                      options={uploads}
                      filterOptions={(options, state) => {
                        const terms = state.inputValue
                          .trim()
                          .toLowerCase()
                          .split(/\s+/)
                          .filter(Boolean)
                        if (!terms.length) return options

                        return options.filter(option => {
                          const haystack = [
                            option.name,
                            option.description,
                            option.owner,
                            ...(option.groups || [])
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .toLowerCase()
                          return terms.every(term => haystack.includes(term))
                        })
                      }}
                      blurOnSelect
                      onChange={(event, value) => { uploadSelected(event, value); setSourcePicker(false) }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Source"
                          placeholder="Select video"
                        />
                      )}
                    />
                  : <Skeleton variant="rounded" width="100%" height={40} />}
              </Box>

          </Stack>
        </Paper>}

        <Box
          className={`editor-review-grid${panelOpen ? '' : ' editor-review-grid-events-hidden'}`}
          sx={{ display: view === 'watch' ? 'grid' : 'none' }}
        >
          <Stack className="editor-viewer-column" sx={{ minWidth: 0 }}>
            <Paper className="editor-viewer-shell" elevation={0}>
              <Box className="editor-monitor-transport">
                <Button size="small" aria-pressed={highestQuality} onClick={() => {
                  const next = !highestQualityRef.current
                  highestQualityRef.current = next
                  setHighestQuality(next)
                  const player = playerRef.current
                  if (player && !player.isDisposed()) applyPlaybackQuality(player.qualityLevels(), next)
                }} title={highestQuality
                  ? 'Always the highest quality, even when the connection is slow (it may pause to buffer). Click for Auto.'
                  : 'Auto plays the highest quality your connection keeps up with, stepping down only when it is slow. Click to always play the highest.'}>
                  Quality: {highestQuality ? 'Highest' : 'Auto'}{currentQuality?.height ? ` (${currentQuality.height}p)` : ''}
                </Button>
                <VideoViewMenu view={videoView} onChange={changeVideoView} onAdjust={() => setAdjustingView(true)} disabled={!playbackUrl} />
                <PlayerStatusCard
                  compact
                  uploadId={uploadId}
                  currentTime={currentTime === null ? null : formatTime(currentTime)}
                  currentRemaining={currentRemaining}
                  currentRate={currentRate}
                  resolution={currentQuality ? `${currentQuality.height} × ${currentQuality.width}` : ''}
                  mark={mark}
                  clippingMode={clipping.active}
                  onToggleClippingMode={clipping.available ? clipping.toggle : undefined}
                  canPlay={Boolean(playbackUrl)}
                  markDisabledReason={partLock}
                  onMark={() => {
                    if (playerRef.current) markSet(playerRef.current.currentTime())
                  }}
                  onMarkEnd={() => {
                    if (playerRef.current) markSet(playerRef.current.currentTime())
                  }}
                  onMarkCancel={cleared}
                  onHelp={onShowHelp}
                />
              </Box>
              {upload && !upload.readyForPlayback
                ? <UploadProcessingStatus upload={upload} />
                : null}

              {upload && playbackUrl
                ? <Box className="editor-player-canvas" sx={{ position: 'relative' }}>
                    <VideoJS
                      options={{
                        autoplay: false,
                        controls: true,
                        responsive: true,
                        fluid: true,
                        preload: 'metadata',
                        aspectRatio: '16:9',
                        playbackRates: [0.25, 0.5, 1, 1.5, 2, 5],
                        sources: playbackUrl,
                        withCredentials: true,
                        html5: {
                          vhs: {
                            withCredentials: true
                          }
                        },
                        debug: true
                      }}
                      onToggleFullscreen={clipping.available ? clipping.toggle : undefined}
                      onReady={handlePlayerReady}
                      onLoadMetaData={handleLoadMetaData}
                      onTimeChange={timeUpdated}
                      onRateChange={rateUpdated}
                      onQualityChange={qualityChanged}
                      onMark={markSet}
                      onClear={cleared}
                      onEdit={() => onEditHighlight()}
                      pauseEvents={eventsPaused || view !== 'watch' || adjustingView}
                      debug="true"
                      view={videoView}
                    />
                    <PlayerTrackOverlay player={playerRef.current} tracks={analysisTracks} seconds={currentTime} />
                    {adjustingView && <VideoViewAdjuster view={videoView} onChange={changeVideoView} onDone={() => setAdjustingView(false)}
                      saveNote={viewCanEdit ? 'Saved for everyone who watches this video.' : 'You can’t edit this video, so this view is kept on this device only.'} />}
                  </Box>
                : !upload
                    ? <Box
                        className="editor-player-placeholder"
                        sx={{
                          aspectRatio: '16 / 9',
                          display: 'grid',
                          placeItems: 'center',
                          color: 'text.secondary'
                        }}
                      >
                        <Typography variant="body2">Choose a source to begin reviewing video.</Typography>
                      </Box>
                    : null}

            </Paper>

            {upload && <Box className="editor-compact-timeline"><UploadTimeline {...timelineProps} variant="tracks" /></Box>}
          </Stack>

          <Stack
            spacing={1}
            id="play-browser"
            className="editor-inspector editor-play-browser"
            sx={{
              display: panelOpen ? 'flex' : 'none',
              minWidth: 0
            }}
          >
            <UploadTimeline {...timelineProps} variant="list" />
            {reviewState.error && <Alert severity="warning" action={<Button onClick={reviewState.retry}>Retry</Button>}>AI review could not be loaded.</Alert>}
            {selectedEntry
              ? <Paper component="section" variant="outlined" aria-label="Selected event details" className="editor-selected-panel" sx={{ overflow: 'hidden', p: selectedEntry.type === 'highlight' ? 0 : 1.25 }}>
                  {selectedEntry.type !== 'highlight' && <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Chip size="small" variant="outlined" label={selectedEntry.type === 'index' ? 'Shift index' : 'AI play'} />
                    <Typography color="text.secondary" className="editor-metric" sx={{ fontSize: 13 }}>{timestamp(selectedEntry.start)}–{timestamp(selectedEntry.end)}</Typography>
                  </Stack>}
                  {selectedEntry.type === 'highlight' && <>
                    <HighlightCard
                      value={highlight?.id === selectedEntry.highlight.id ? highlight : selectedEntry.highlight}
                      embedded
                      onEdit={() => onEditHighlight(highlight?.id === selectedEntry.highlight.id ? highlight : selectedEntry.highlight)}
                      onDelete={() => onDeleteHighlight(highlight?.id === selectedEntry.highlight.id ? highlight : selectedEntry.highlight)}
                      onRefresh={() => onRefreshHighlight(highlight?.id === selectedEntry.highlight.id ? highlight : selectedEntry.highlight)}
                      onPrev={onPrev}
                      onNext={onNext}
                      isPrev={highlights.findIndex(item => item.id === selectedEntry.highlight.id) > 0}
                      isNext={highlights.findIndex(item => item.id === selectedEntry.highlight.id) + 1 < highlights.length}
                      canShareWatchLink={Boolean(user && upload?.owner === user.username)}
                      lockedReason={partLock}
                    />
                    {/* AI coaching on a clip is an admin-only beta (D18); the API enforces it too. Its
                        status, "View coaching" and retry live on the clip itself (D20). */}
                    {isAdmin && <ClipCoachingStatus uploadId={uploadId} highlight={selectedEntry.highlight} refreshKey={clipCoachingRefresh}
                      onRequest={() => { setAnalysisRange(selectedEntry.highlight); setAnalysisOpen(true) }} onView={openJobReport} />}
                  </>}
                  {selectedEntry.type === 'index' && <Stack spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedEntry.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedEntry.description || 'Indexed game sequence.'}</Typography>
                    <Button size="small" onClick={() => seekReview(selectedEntry.start)} sx={{ alignSelf: 'flex-start' }}>Watch this shift</Button>
                  </Stack>}
                  {selectedEntry.type === 'play' && selectedEntry.event
                    ? <AIEvent compact={false} event={selectedEntry.event} feedback={analysisReview ? analysisFeedback[selectedEntry.event.id] : reviewState.data?.feedback?.[selectedEntry.event.id]} uploadId={uploadId} runId={timelineReview?.run_id || ''} onSeek={seekReview} onCreateHighlight={partLock ? undefined : saveAIHighlight} onFeedbackSaved={analysisReview ? setAnalysisFeedback : reviewState.saveFeedback} />
                    : selectedEntry.type === 'play' && <Stack spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedEntry.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{selectedEntry.description}</Typography>
                        <Button size="small" onClick={() => seekReview(selectedEntry.start)} sx={{ alignSelf: 'flex-start' }}>Watch this play</Button>
                      </Stack>}
                </Paper>
              : <Paper variant="outlined" sx={{ px: 1.5, py: 0.75 }}><Typography variant="body2" color="text.secondary">Select an event to see its details.</Typography></Paper>}

            <Box component="details"><Typography component="summary" variant="body2" sx={{ cursor: 'pointer', px: 1, py: 0.75 }}>Upload details</Typography>
            {upload
              ? <UploadCard
                  upload={upload}
                  onEdit={onEditUpload}
                  lockedReason={partLock}
                />
              : <Paper variant="outlined" className="editor-inspector-panel" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Select a source to inspect its details.
                  </Typography>
                </Paper>}
            </Box>
          </Stack>
        </Box>
        {view === 'report' && <AIReview uploadId={uploadId} title={upload?.name} thumbnail={upload?.thumbnailUrl} data={reportData} clipName={reportData?.clip ? highlightForRange(highlights, reportData.start_seconds, reportData.end_seconds)?.name : undefined} isAdmin={isAdmin} onRatingsSaved={analysisReview ? rows => setAnalysisExtras(extras => ({ ...extras, coaching_feedback: rows })) : reviewState.saveRatings} error={!analysisReview && reviewState.error} onRetry={reviewState.retry} onSeek={seekReview} onCreateHighlight={partLock ? undefined : saveAIHighlight} onFeedbackSaved={analysisReview ? setAnalysisFeedback : reviewState.saveFeedback} />}
        {uploadId && <VideoAnalysis uploadId={uploadId} open={analysisOpen} initialRange={analysisRange} isAdmin={isAdmin}
          clipLabel={(start, end) => clipRunLabel(highlightForRange(highlights, start, end)?.name, start, end)}
          onStarted={() => setClipCoachingRefresh(n => n + 1)}
          onClose={() => { setAnalysisOpen(false); setClipCoachingRefresh(n => n + 1) }} onIdentify={context => { setReferenceContext(context); setView('watch'); playerRef.current?.pause() }}
          onPlay={clip => { setAnalysisTracks(clip.tracks); analysisClipEnd.current = clip.end_seconds; setView('watch'); seekPlayerToHighlight(playerRef.current, { start: clip.start_seconds, end: clip.end_seconds }); playerRef.current?.play()?.catch(() => {}) }}
          onSave={saveAIHighlight} saveDisabledReason={partLock} onReport={openJobReport} />}
      </Stack>
      </TrapFocus>
      {
        newHighlight
          ? <HighlightDialog
            isOpen={true}
            value={newHighlight}
            existingHighlights={highlights}
            allowedGroups={upload && upload.groups ? upload.groups : []}
            onSave={onCreateHighlight}
            onCancel={onCancelCreateHighlight}
          />
          : null
      }
      {
        editingUpload
          ? <UploadDialog
            title="Edit Upload"
            isOpen={true}
            upload={upload}
            onSave={onSaveUpload}
            onCancel={onCancelSaveUpload}
          />
          : null
      }
      {
        showHelp
          ? <ControlsDialog
            isOpen={showHelp}
            onClose={onHelpClose}/>
          : null
      }
      {
        editingHighlight
          ? <HighlightDialog
            title="Edit Highlight"
            isOpen={true}
            value={highlight}
            existingHighlights={highlights}
            allowedGroups={upload && upload.groups ? upload.groups : []}
            onSave={onSaveHighlight}
            onCancel={onCancelSaveHighlight}
          />
          : null
      }
      <Messages message={message} onClose={() => setMessage(null)}/>
    </>
  )
}

export default Upload
